import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Trash2 } from "react-feather";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {
  useGetUserQuery,
  useUpdateUserMutation,
  useAssignUserRoleMutation,
  useRemoveUserRoleMutation,
  useAssignUserBranchMutation,
  useRemoveUserBranchMutation,
  useGrantUserPermissionOverrideMutation,
  useClearUserPermissionOverrideMutation,
} from "../usersApi";
import { useGetRolesQuery } from "../../roles/rolesApi";
import { useGetBranchesQuery, useGetPermissionsQuery } from "../../admin/adminApi";
import { useClaims } from "../../auth/useClaims";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { paths } from "../../../routes/paths";
import ResetPasswordModal from "../components/ResetPasswordModal";

const UserDetail = () => {
  const { userId } = useParams();
  const { data, isLoading } = useGetUserQuery(userId);
  const { isAllBranches: callerIsAllBranches } = useClaims();
  const { data: allRoles = [] } = useGetRolesQuery();
  const { data: allBranches = [] } = useGetBranchesQuery();
  const { data: allPermissions = [] } = useGetPermissionsQuery();

  const [updateUser] = useUpdateUserMutation();
  const [assignRole] = useAssignUserRoleMutation();
  const [removeRole] = useRemoveUserRoleMutation();
  const [assignBranch] = useAssignUserBranchMutation();
  const [removeBranch] = useRemoveUserBranchMutation();
  const [grantOverride] = useGrantUserPermissionOverrideMutation();
  const [clearOverride] = useClearUserPermissionOverrideMutation();

  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [overridePermissionId, setOverridePermissionId] = useState("");
  const [overrideIsGranted, setOverrideIsGranted] = useState("grant");
  const [overrideReason, setOverrideReason] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const { profile, branches, roles, effectivePermissions, overrides } = data;
  const isEmployeeLinked = Boolean(profile.employeeId);

  const assignedRoleIds = new Set(roles.map((r) => r.roleId));
  const assignableRoles = allRoles.filter(
    (r) =>
      !assignedRoleIds.has(r.roleId) &&
      (!r.grantsAllBranchAccess || callerIsAllBranches)
  );

  const assignedBranchIds = new Set(branches.map((b) => b.branchId));
  const assignableBranches = allBranches.filter(
    (b) => !assignedBranchIds.has(b.branchId)
  );

  const handleToggleActive = async () => {
    try {
      await updateUser({ userId, isActive: !profile.isActive }).unwrap();
      toast.success(profile.isActive ? "User disabled" : "User enabled");
    } catch (error) {
      toast.error(error?.message || "Could not update user");
    }
  };

  const handleAssignRole = async () => {
    if (!selectedRoleId) return;
    try {
      await assignRole({ userId, roleId: Number(selectedRoleId) }).unwrap();
      setSelectedRoleId("");
      toast.success("Role assigned");
    } catch (error) {
      toast.error(error?.message || "Could not assign role");
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      await removeRole({ userId, roleId }).unwrap();
      toast.success("Role removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove role");
    }
  };

  const handleAssignBranch = async () => {
    if (!selectedBranchId) return;
    try {
      await assignBranch({ userId, branchId: Number(selectedBranchId) }).unwrap();
      setSelectedBranchId("");
      toast.success("Branch assigned");
    } catch (error) {
      toast.error(error?.message || "Could not assign branch");
    }
  };

  const handleRemoveBranch = async (branchId) => {
    try {
      await removeBranch({ userId, branchId }).unwrap();
      toast.success("Branch removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove branch");
    }
  };

  const handleAddOverride = async () => {
    if (!overridePermissionId) return;
    try {
      await grantOverride({
        userId,
        permissionId: Number(overridePermissionId),
        isGranted: overrideIsGranted === "grant",
        reason: overrideReason.trim() || undefined,
      }).unwrap();
      setOverridePermissionId("");
      setOverrideReason("");
      toast.success("Permission override saved");
    } catch (error) {
      toast.error(error?.message || "Could not save override");
    }
  };

  const handleClearOverride = async (permissionId) => {
    const confirmed = await confirmDelete({
      title: "Clear this override?",
      text: "The user will fall back to their designation/role default for this permission.",
    });
    if (!confirmed) return;
    try {
      await clearOverride({ userId, permissionId }).unwrap();
      toast.success("Override cleared");
    } catch (error) {
      toast.error(error?.message || "Could not clear override");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.users} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Users
              </Link>
              <h4>{profile.fullName}</h4>
              <h6>@{profile.username}</h6>
            </div>
          </div>
          <div className="page-btn">
            <button
              type="button"
              className="btn btn-outline-primary me-2"
              onClick={() => setShowResetPassword(true)}
            >
              Reset Password
            </button>
            <button
              type="button"
              className={`btn ${profile.isActive ? "btn-outline-danger" : "btn-outline-success"}`}
              onClick={handleToggleActive}
            >
              {profile.isActive ? "Disable User" : "Enable User"}
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="mb-3">Profile</h5>
                <p className="mb-1">
                  <strong>Designation:</strong>{" "}
                  {profile.designationName || "-"}
                </p>
                <p className="mb-1">
                  <strong>Status:</strong>{" "}
                  <span className={`badge ${profile.isActive ? "badge-linesuccess" : "badge-linedanger"}`}>
                    {profile.isActive ? "Active" : "Disabled"}
                  </span>
                </p>
                <p className="mb-1">
                  <strong>Must change password:</strong>{" "}
                  {profile.mustChangePassword ? "Yes" : "No"}
                </p>
                <p className="mb-1">
                  <strong>Last login:</strong>{" "}
                  {profile.lastLoginDate
                    ? dayjs(profile.lastLoginDate).format("DD MMM YYYY HH:mm")
                    : "Never"}
                </p>
                <p className="mb-0">
                  <strong>Created:</strong>{" "}
                  {profile.createdDate
                    ? dayjs(profile.createdDate).format("DD MMM YYYY")
                    : "-"}
                </p>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h5 className="mb-3">Branches</h5>
                {isEmployeeLinked ? (
                  <p className="text-muted mb-0">
                    Branch is derived from the linked employee and cannot be
                    changed here.
                  </p>
                ) : null}
                <div className="mb-3">
                  {branches.map((b) => (
                    <span
                      key={b.branchId}
                      className="badge badge-linesuccess me-2 mb-2 d-inline-flex align-items-center"
                    >
                      {b.branchCode}
                      {!isEmployeeLinked && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-2 text-danger"
                          onClick={() => handleRemoveBranch(b.branchId)}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </span>
                  ))}
                  {branches.length === 0 && (
                    <p className="text-muted mb-0">No branches assigned.</p>
                  )}
                </div>
                {!isEmployeeLinked && (
                  <div className="d-flex gap-2">
                    <select
                      className="form-control"
                      value={selectedBranchId}
                      onChange={(e) => setSelectedBranchId(e.target.value)}
                    >
                      <option value="">Select branch to add</option>
                      {assignableBranches.map((b) => (
                        <option key={b.branchId} value={b.branchId}>
                          {b.branchCode}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAssignBranch}
                      disabled={!selectedBranchId}
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h5 className="mb-3">Roles</h5>
                <div className="mb-3">
                  {roles.map((r) => (
                    <span
                      key={r.roleId}
                      className="badge badge-linesuccess me-2 mb-2 d-inline-flex align-items-center"
                    >
                      {r.roleName}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ms-2 text-danger"
                        onClick={() => handleRemoveRole(r.roleId)}
                      >
                        <Trash2 size={12} />
                      </button>
                    </span>
                  ))}
                  {roles.length === 0 && (
                    <p className="text-muted mb-0">No roles assigned.</p>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <select
                    className="form-control"
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                  >
                    <option value="">Select role to add</option>
                    {assignableRoles.map((r) => (
                      <option key={r.roleId} value={r.roleId}>
                        {r.roleName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAssignRole}
                    disabled={!selectedRoleId}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="mb-3">Effective Permissions</h5>
                <p className="text-muted">
                  Computed from designation defaults + role grants +
                  overrides.
                </p>
                <div>
                  {effectivePermissions.map((p) => (
                    <span key={p.permissionId} className="badge badge-linesuccess me-2 mb-2">
                      {p.permissionCode}
                    </span>
                  ))}
                  {effectivePermissions.length === 0 && (
                    <p className="text-muted mb-0">No effective permissions.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h5 className="mb-3">Permission Overrides</h5>
                <div className="table-responsive mb-3">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Permission</th>
                        <th>Type</th>
                        <th>Reason</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {overrides.map((o) => (
                        <tr key={o.permissionId}>
                          <td>{o.permissionCode}</td>
                          <td>
                            <span className={`badge ${o.isGranted ? "badge-linesuccess" : "badge-linedanger"}`}>
                              {o.isGranted ? "Grant" : "Revoke"}
                            </span>
                          </td>
                          <td>{o.reason || "-"}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-danger"
                              onClick={() => handleClearOverride(o.permissionId)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {overrides.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-muted">
                            No overrides.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <h6 className="mb-2">Add Override</h6>
                <div className="row g-2 align-items-end">
                  <div className="col-md-5">
                    <select
                      className="form-control"
                      value={overridePermissionId}
                      onChange={(e) => setOverridePermissionId(e.target.value)}
                    >
                      <option value="">Select permission</option>
                      {allPermissions.map((p) => (
                        <option key={p.permissionId} value={p.permissionId}>
                          {p.moduleCode} · {p.permissionCode}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-control"
                      value={overrideIsGranted}
                      onChange={(e) => setOverrideIsGranted(e.target.value)}
                    >
                      <option value="grant">Grant</option>
                      <option value="revoke">Revoke</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Reason (optional)"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddOverride}
                      disabled={!overridePermissionId}
                    >
                      Save Override
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ResetPasswordModal
        userId={showResetPassword ? userId : null}
        onClose={() => setShowResetPassword(false)}
      />
    </div>
  );
};

export default UserDetail;
